import { type DerivedState, type GameEvent, reduce } from "@dd/engine";
import type { CaseV1Data } from "@dd/schema";
import { create } from "zustand";

/**
 * Facade React ↔ engine (stack §6): la UI SOLO habla con el dominio a través de
 * `append(event)` y lee `derived`. El engine no importa React; este store es el
 * único punto de contacto.
 *
 * `derived` se recalcula incrementalmente: reduce() es pura, así que aplicar el
 * log completo o solo el evento nuevo sobre el estado previo da lo mismo — pero
 * mantener el log completo como fuente de verdad hace el replay (saves, telemetría)
 * trivial. Con ~cientos de eventos por partida, re-reducir todo es barato; si algún
 * día duele, se optimiza aquí sin tocar consumidores.
 */

interface GameStore {
  kase: CaseV1Data | null;
  events: GameEvent[];
  derived: DerivedState | null;
  /** Carga un caso y resetea (o reproduce un log guardado — el save ES el log) */
  loadCase: (kase: CaseV1Data, replayEvents?: GameEvent[]) => void;
  /** Único camino de mutación: append al log + re-derivar */
  append: (event: GameEvent) => void;
}

export const useGameStore = create<GameStore>()((set, get) => ({
  kase: null,
  events: [],
  derived: null,

  loadCase: (kase, replayEvents = []) =>
    set({ kase, events: replayEvents, derived: reduce(replayEvents, kase) }),

  append: (event) => {
    const { kase, events } = get();
    if (!kase) return; // sin caso cargado no hay dominio que mutar
    const next = [...events, event];
    set({ events: next, derived: reduce(next, kase) });
  },
}));
